import React, { useEffect, useState } from 'react';
import client, { apiCall } from '../../api/client';
import DataTable from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import Loader from '../../components/Loader';

function apiOrigin() {
  const base = client.defaults.baseURL || '';
  return base.replace(/\/api\/?$/, '');
}

export default function Deposits() {
  const [deposits, setDeposits] = useState([]);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [reviewing, setReviewing] = useState(null); // { row, action: 'approve' | 'reject' }
  const [creditedAmount, setCreditedAmount] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = (status = statusFilter) => {
    setLoading(true);
    apiCall(client.get('/wallet/deposit/admin/get_list', { params: status ? { status } : {} }))
      .then(setDeposits)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => load(statusFilter), [statusFilter]);

  const openReview = (row, action) => {
    setReviewing({ row, action });
    setCreditedAmount(String(row.amount));
    setAdminNote('');
    setError('');
  };

  const handleReview = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (reviewing.action === 'approve') {
        await apiCall(client.patch('/wallet/deposit/admin/approve', {
          uniqueId: reviewing.row.uniqueId, creditedAmount: +creditedAmount
        }));
      } else {
        await apiCall(client.patch('/wallet/deposit/admin/reject', {
          uniqueId: reviewing.row.uniqueId, adminNote
        }));
      }
      setReviewing(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: 'userName', label: 'User', render: (row) => row.userName || row.userId },
    { key: 'userEmail', label: 'Email' },
    { key: 'amount', label: 'Claimed', render: (row) => `$${row.amount}` },
    { key: 'txHash', label: 'Tx hash', render: (row) => <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 }}>{row.txHash}</span> },
    { key: 'proofUrl', label: 'Proof', render: (row) => <a href={apiOrigin() + row.proofUrl} target="_blank" rel="noreferrer">View</a> },
    { key: 'status', label: 'Status', render: (row) => <StatusBadge value={row.status} /> },
    { key: 'creditedAmount', label: 'Credited', render: (row) => row.creditedAmount != null ? `$${row.creditedAmount}` : '—' },
    {
      key: 'actions', label: '', render: (row) => row.status === 'PENDING' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={() => openReview(row, 'approve')}>Approve</button>
          <button className="btn btn-danger-outline btn-sm" onClick={() => openReview(row, 'reject')}>Reject</button>
        </div>
      )
    },
  ];

  return (
    <div>
      <div className="page-header-row">
        <h1>Deposits</h1>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="">All</option>
        </select>
      </div>
      {error && !reviewing && <div className="form-error">{error}</div>}

      <div className="card">
        {loading ? (
          <Loader label="Loading deposits..." />
        ) : (
          <DataTable columns={columns} rows={deposits} emptyMessage="No deposit requests found." />
        )}
      </div>

      {reviewing && (
        <div className="modal-backdrop" onClick={() => setReviewing(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>{reviewing.action === 'approve' ? 'Approve' : 'Reject'} deposit</h3>
            <p className="profile-section-desc">
              {reviewing.row.userName || reviewing.row.userId} claims ${reviewing.row.amount} — tx {reviewing.row.txHash}
            </p>
            {error && <div className="form-error">{error}</div>}
            <form onSubmit={handleReview}>
              {reviewing.action === 'approve' ? (
                <div className="form-group" style={{ marginBottom: 20 }}>
                  <label>Amount to credit ($)</label>
                  <input
                    type="number" min="0" step="0.01" required
                    value={creditedAmount}
                    onChange={(e) => setCreditedAmount(e.target.value)}
                  />
                </div>
              ) : (
                <div className="form-group" style={{ marginBottom: 20 }}>
                  <label>Note (optional)</label>
                  <input value={adminNote} onChange={(e) => setAdminNote(e.target.value)} placeholder="Reason for rejection" />
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className={`btn ${reviewing.action === 'approve' ? 'btn-primary' : 'btn-danger-outline'}`} disabled={saving}>
                  {saving ? 'Saving...' : reviewing.action === 'approve' ? 'Approve & credit' : 'Reject'}
                </button>
                <button type="button" className="btn btn-outline" onClick={() => setReviewing(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
